import {
  AutomationEventRunSkipReasonType,
  AutomationEventRunStatusType,
  AutomationKindType,
  AutomationStatusType,
  AutomationTriggerType,
} from "@/types/graphql/partner.generated";
import { JOURNEY_FALLBACK_KEY, JOURNEY_TRIGGER_KEYS } from "./journeyLabels";
import { labelKeyFor } from "./labelKeyFor";

// Помощник живёт в своём модуле (без цикла импорта с journeyLabels);
// реэкспорт для прежних импортов фичи.
export { labelKeyFor };

export const AUTOMATION_STATUS_KEYS: Record<AutomationStatusType, string> = {
  [AutomationStatusType.DraftType]: "automations.status.DRAFT",
  [AutomationStatusType.ActiveType]: "automations.status.ACTIVE",
  [AutomationStatusType.PausedType]: "automations.status.PAUSED",
  [AutomationStatusType.ArchivedType]: "automations.status.ARCHIVED",
};

/**
 * Текст для статуса, которого нет в карте: рост enum на бэкенде (фаза 4)
 * не должен показать пустой чип или ключ `undefined` до следующего
 * релиза кабинета.
 */
export const AUTOMATION_STATUS_FALLBACK_KEY = "automations.status.UNKNOWN";

export const AUTOMATION_FILTER_STATUSES: AutomationStatusType[] = [
  AutomationStatusType.DraftType,
  AutomationStatusType.ActiveType,
  AutomationStatusType.PausedType,
  AutomationStatusType.ArchivedType,
];

/** Подписи вида сценария: колонка «Вид» и фильтр списка. */
export const KIND_KEYS: Record<AutomationKindType, string> = {
  [AutomationKindType.BroadcastType]: "menu.broadcasts",
  [AutomationKindType.PlatformEventType]: "automations.kind.PLATFORM_EVENT",
  [AutomationKindType.JourneyType]: "automations.kind.JOURNEY",
};

/**
 * Виды на странице автоматизаций и их порядок в фильтре «Вид» (вид
 * кабинета, не правило: рассылки живут на своей странице).
 */
export const AUTOMATION_LIST_KINDS: AutomationKindType[] = [
  AutomationKindType.PlatformEventType,
  AutomationKindType.JourneyType,
];

/** Триггеры автоматизаций по событию платформы (не рассылки и не цепочки). */
export type PlatformTrigger =
  | AutomationTriggerType.ProductPublishedType
  | AutomationTriggerType.StreamAnnouncedType
  | AutomationTriggerType.ContentPublishedType
  | AutomationTriggerType.PostPublishedType;

export const TRIGGER_KEYS: Record<PlatformTrigger, string> = {
  [AutomationTriggerType.ProductPublishedType]:
    "automations.trigger.PRODUCT_PUBLISHED",
  [AutomationTriggerType.StreamAnnouncedType]:
    "automations.trigger.STREAM_ANNOUNCED",
  [AutomationTriggerType.ContentPublishedType]:
    "automations.trigger.CONTENT_PUBLISHED",
  [AutomationTriggerType.PostPublishedType]:
    "automations.trigger.POST_PUBLISHED",
};

/** Порядок подписей событий платформы (вид кабинета), те же ключи, что TRIGGER_KEYS. */
export const PLATFORM_TRIGGERS: PlatformTrigger[] = [
  AutomationTriggerType.ProductPublishedType,
  AutomationTriggerType.StreamAnnouncedType,
  AutomationTriggerType.ContentPublishedType,
  AutomationTriggerType.PostPublishedType,
];

export const isPlatformTrigger = (
  value: AutomationTriggerType | null | undefined
): value is PlatformTrigger =>
  PLATFORM_TRIGGERS.some((trigger) => trigger === value);

/**
 * Объединённая карта подписей триггеров автоматизаций и цепочек.
 * journeyLabels берёт отсюда только тип PlatformTrigger (стирается при
 * сборке), поэтому на загрузке модуля его карта уже готова.
 */
const TRIGGER_LABEL_KEYS: Record<
  Exclude<AutomationTriggerType, AutomationTriggerType.ScheduledType>,
  string
> = { ...TRIGGER_KEYS, ...JOURNEY_TRIGGER_KEYS };

/** Подпись любого триггера автоматизации или цепочки; неизвестное значение: общий текст. */
export function triggerKey(
  value: AutomationTriggerType | null | undefined
): string {
  return value === AutomationTriggerType.ScheduledType
    ? JOURNEY_FALLBACK_KEY
    : labelKeyFor(TRIGGER_LABEL_KEYS, value, JOURNEY_FALLBACK_KEY);
}

/**
 * Событие сценария для строки списка: Automation.triggerEvent (сервер
 * читает его из узла TRIGGER, список не тянет граф, супер-ревью 2).
 * SCHEDULED это рассылка, в списке автоматизаций события нет.
 */
export function listTrigger(
  event: AutomationTriggerType | null | undefined
): AutomationTriggerType | null {
  return event && event !== AutomationTriggerType.ScheduledType ? event : null;
}

/** Подписи варианта в диалоге создания автоматизации. */
export interface AutomationTemplateLabels {
  titleKey: string;
  descriptionKey: string;
}

/** «Пустая автоматизация»: создание без шаблона (templateKey null). */
export const BLANK_TEMPLATE_LABELS: AutomationTemplateLabels = {
  titleKey: "automations.templates.blank.title",
  descriptionKey: "automations.templates.blank.description",
};

/** «Пустая цепочка»: создание цепочки без шаблона (templateKey null). */
export const BLANK_JOURNEY_TEMPLATE_LABELS: AutomationTemplateLabels = {
  titleKey: "automations.templates.blankJourney.title",
  descriptionKey: "automations.templates.blankJourney.description",
};

/** Подписи шаблона по ключу: automations.templates.<ключ>.title и .description. */
const templateEntry = (key: string): [string, AutomationTemplateLabels] => [
  key,
  {
    titleKey: `automations.templates.${key}.title`,
    descriptionKey: `automations.templates.${key}.description`,
  },
];

/**
 * Подписи шаблонов по ключу шаблона бэкенда. Сам список и порядок
 * шаблонов приходят с сервера (automationGraphRules.templates), здесь
 * только тексты.
 */
export const AUTOMATION_TEMPLATE_LABELS: ReadonlyMap<
  string,
  AutomationTemplateLabels
> = new Map([
  [
    "newProduct",
    {
      titleKey: "automations.templates.newProduct.title",
      descriptionKey: "automations.templates.newProduct.description",
    },
  ],
  [
    "streamAnnouncement",
    {
      titleKey: "automations.templates.streamAnnouncement.title",
      descriptionKey: "automations.templates.streamAnnouncement.description",
    },
  ],
  [
    "newVideo",
    {
      titleKey: "automations.templates.newVideo.title",
      descriptionKey: "automations.templates.newVideo.description",
    },
  ],
  [
    "newPost",
    {
      titleKey: "automations.templates.newPost.title",
      descriptionKey: "automations.templates.newPost.description",
    },
  ],
  // Шаблоны цепочек (automationGraphRules(kind: JOURNEY).templates).
  ...[
    "registeredNoPurchase",
    "purchasedNotWatched",
    "leftMidway",
    "abandonedCart",
    "subscriptionEnding",
    "autoRenewOff",
    "subscriptionWinBack",
    "insufficientFunds",
    "cardExpired",
    "watchedBuyNext",
    "ticketToPass",
    "cardDeclined",
  ].map(templateEntry),
]);

/**
 * Подсказка о пересечении цепочек под описанием шаблона (зритель может
 * попасть в соседнюю цепочку) или о предусловии шаблона.
 */
export const TEMPLATE_HINT_KEYS: ReadonlyMap<string, string> = new Map([
  ["insufficientFunds", "automations.journeyTemplates.insufficientFunds.hint"],
  [
    "subscriptionWinBack",
    "automations.journeyTemplates.subscriptionWinBack.hint",
  ],
  // Подсказка «проверьте, что абонемент заведён» у шаблона «С билета на абонемент».
  ["ticketToPass", "automations.journeyTemplates.ticketToPass.hint"],
]);

/** Шаблон, которого кабинет ещё не знает (новее сервера): общий текст. */
export const AUTOMATION_TEMPLATE_FALLBACK_LABELS: AutomationTemplateLabels = {
  titleKey: "automations.templates.UNKNOWN.title",
  descriptionKey: "automations.templates.UNKNOWN.description",
};

export const templateLabels = (key: string): AutomationTemplateLabels =>
  AUTOMATION_TEMPLATE_LABELS.get(key) ?? AUTOMATION_TEMPLATE_FALLBACK_LABELS;

/**
 * Текст статуса запуска события (AutomationEventRun.status): ключ DONE
 * («N адресатов») читается с интерполяцией `{{count}}`, у остальных
 * `{{count}}` просто не используется в тексте.
 */
export const EVENT_RUN_STATUS_KEYS: Record<
  AutomationEventRunStatusType,
  string
> = {
  [AutomationEventRunStatusType.RunningType]: "automations.runs.running",
  [AutomationEventRunStatusType.DoneType]: "automations.runs.enrolled",
  [AutomationEventRunStatusType.FailedType]: "automations.runs.failed",
  [AutomationEventRunStatusType.SkippedType]: "automations.runs.skipped",
};

/**
 * Статус запуска события вне известных значений (рост enum на бэкенде
 * раньше релиза кабинета): нейтральный текст «статус неизвестен», а не
 * пустой чип и не ложное «N адресатов» (фикс-раунд 1, M2: прежний
 * фоллбэк на DONE выглядел успехом для статуса вроде CANCELLED).
 */
export const EVENT_RUN_STATUS_FALLBACK_KEY = "automations.runs.statusUnknown";

/**
 * Причина, почему событие не запустило автоматизацию
 * (AutomationEventRun.skipReason): свои тексты, а не переиспользование
 * broadcasts.skip.* (фикс-раунд 1): у события автоматизации свой смысл,
 * OBJECT_UNAVAILABLE это любой недоступный объект (снят с публикации,
 * скрыт или удалён), AUTOMATION_INACTIVE это любой статус кроме ACTIVE
 * (пауза или архив), а не конкретно «поставлена на паузу».
 */
export const EVENT_RUN_SKIP_REASON_KEYS: Record<
  AutomationEventRunSkipReasonType,
  string
> = {
  [AutomationEventRunSkipReasonType.RateLimitType]:
    "automations.runs.skip.RATE_LIMIT",
  [AutomationEventRunSkipReasonType.ObjectUnavailableType]:
    "automations.runs.skip.OBJECT_UNAVAILABLE",
  [AutomationEventRunSkipReasonType.AutomationInactiveType]:
    "automations.runs.skip.AUTOMATION_INACTIVE",
  [AutomationEventRunSkipReasonType.MarketingDisabledType]:
    "automations.runs.skip.MARKETING_DISABLED",
};

/**
 * Причина пропуска вне EVENT_RUN_SKIP_REASON_KEYS (рост enum на бэкенде
 * раньше релиза кабинета): текст вместо `undefined` в подписи причины.
 */
export const AUTOMATION_EVENT_RUN_SKIP_REASON_FALLBACK_KEY =
  "automations.runs.skip.UNKNOWN";
